import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { mediaShell } from "../../../styles/mediaShell";

function MediaFullscreenViewer({ media, onClose }) {
    const containerRef = useRef(null);
    const bodyStyleRef = useRef(null);

    // Safariかどうかを判定する関数
    const isSafari = () => {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    };

    useEffect(() => {
        const enterFullscreen = async () => {
            // Safariの場合は独自の全画面表示を使用
            if (isSafari()) {
                // body スクロール無効化（旧 body.fullscreen-mode CSS）
                bodyStyleRef.current = {
                    overflow: document.body.style.overflow,
                    position: document.body.style.position,
                    width: document.body.style.width,
                    height: document.body.style.height,
                };
                document.body.style.overflow = "hidden";
                document.body.style.position = "fixed";
                document.body.style.width = "100%";
                document.body.style.height = "100%";
                document.body.classList.add("fullscreen-mode");

                // Safari用：強制的に最前面に表示（複数回実行）
                const forceFullscreen = () => {
                    if (containerRef.current) {
                        const element = containerRef.current;
                        element.style.zIndex = "2147483647";
                        element.style.position = "fixed";
                        element.style.top = "0";
                        element.style.left = "0";
                        element.style.width = "100vw";
                        element.style.height = "100vh";
                        element.style.transform = "translate3d(0, 0, 0)";
                        element.style.webkitTransform = "translate3d(0, 0, 0)";
                        element.style.backfaceVisibility = "hidden";
                        element.style.webkitBackfaceVisibility = "hidden";

                        // 親要素も含めて強制的に最前面に
                        let parent = element.parentElement;
                        while (parent && parent !== document.body) {
                            parent.style.zIndex = "2147483646";
                            parent.style.position = "relative";
                            parent = parent.parentElement;
                        }
                    }
                };

                // 即座に実行
                forceFullscreen();

                // レンダリング後に再実行
                setTimeout(forceFullscreen, 0);
                setTimeout(forceFullscreen, 10);
                setTimeout(forceFullscreen, 50);

                return;
            }

            // その他のブラウザでは標準のrequestFullscreenを使用
            if (containerRef.current?.requestFullscreen) {
                try {
                    await containerRef.current.requestFullscreen();
                } catch (e) {
                    console.warn("フルスクリーンに失敗:", e);
                }
            }
        };

        enterFullscreen();

        const handleFullscreenChange = () => {
            if (!document.fullscreenElement && !isSafari()) {
                onClose();
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        // Safari以外の場合のみfullscreenchangeイベントを監視
        if (!isSafari()) {
            document.addEventListener("fullscreenchange", handleFullscreenChange);
        }
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            if (!isSafari()) {
                document.removeEventListener("fullscreenchange", handleFullscreenChange);
            }
            document.removeEventListener("keydown", handleKeyDown);
            document.body.classList.remove("fullscreen-mode");
            if (bodyStyleRef.current) {
                document.body.style.overflow = bodyStyleRef.current.overflow;
                document.body.style.position = bodyStyleRef.current.position;
                document.body.style.width = bodyStyleRef.current.width;
                document.body.style.height = bodyStyleRef.current.height;
                bodyStyleRef.current = null;
            }
        };
    }, [onClose]);

    if (media.media_type !== "image") return null;

    const fullscreenContent = (
        <div ref={containerRef} className={mediaShell.fullscreenRoot} onClick={onClose}>
            <div className={mediaShell.fullscreenBox}>
                <img src={media.url} alt="media" className={mediaShell.fullscreenMedia} />
            </div>
        </div>
    );

    // Safariの場合はdocument.bodyに直接Portalで描画
    if (isSafari()) {
        return createPortal(fullscreenContent, document.body);
    }

    return fullscreenContent;
}

export default MediaFullscreenViewer;
